import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Cpu, RefreshCw, Network, Zap, Clock, Building2, Upload, X,
  RotateCcw, XCircle, Trash2, FileText, Mail, FolderOpen, Copy,
  Search, ChevronDown, BookOpen, HelpCircle, ChevronRight, Info,
} from 'lucide-react';

export default function ParserQueuePage({ accounts = [] }) {
  const [subTab, setSubTab] = useState('queue');

  // Parser Jobs state
  const [parserJobs, setParserJobs] = useState([]);
  const [parserJobsLoading, setParserJobsLoading] = useState(false);
  const [parserJobsLoadingMore, setParserJobsLoadingMore] = useState(false);
  const [parserJobsTotalCount, setParserJobsTotalCount] = useState(0);
  const [parserJobsSkip, setParserJobsSkip] = useState(0);
  const PAGE_SIZE = 50;
  const [parserJobsStats, setParserJobsStats] = useState(null);
  const [parserJobsFilters, setParserJobsFilters] = useState({
    status: 'all', account_id: 'all', source: 'all', processing_method: 'all',
  });
  const [jobIdSearch, setJobIdSearch] = useState('');
  const [jobIdSearchInput, setJobIdSearchInput] = useState('');

  // SQS state
  const [sqsStatus, setSqsStatus] = useState(null);
  const [sqsLoading, setSqsLoading] = useState(false);
  const [bulkReprocessLoading, setBulkReprocessLoading] = useState(false);

  // Docs drawer removed - all docs consolidated into Technical Docs tab

  const fetchParserJobs = useCallback(async (append = false) => {
    if (append) {
      setParserJobsLoadingMore(true);
    } else {
      setParserJobsLoading(true);
    }
    try {
      const currentSkip = append ? parserJobsSkip : 0;
      const params = new URLSearchParams();
      if (parserJobsFilters.status !== 'all') params.append('status', parserJobsFilters.status);
      if (parserJobsFilters.account_id !== 'all') params.append('account_id', parserJobsFilters.account_id);
      if (parserJobsFilters.source !== 'all') params.append('source', parserJobsFilters.source);
      if (parserJobsFilters.processing_method !== 'all') params.append('processing_method', parserJobsFilters.processing_method);
      params.append('limit', String(PAGE_SIZE));
      params.append('skip', String(currentSkip));
      const { data } = await api.get(`/super-admin/parser-jobs?${params.toString()}`);
      if (append) {
        setParserJobs(prev => [...prev, ...(data.jobs || [])]);
      } else {
        setParserJobs(data.jobs || []);
      }
      setParserJobsTotalCount(data.total_count || 0);
      setParserJobsSkip(currentSkip + (data.jobs?.length || 0));
    } catch { toast.error('Failed to load parser jobs'); }
    finally {
      setParserJobsLoading(false);
      setParserJobsLoadingMore(false);
    }
  }, [parserJobsFilters, parserJobsSkip]);

  const fetchParserJobsStats = useCallback(async () => {
    try {
      const { data } = await api.get('/super-admin/parser-jobs/stats');
      setParserJobsStats(data);
    } catch { console.error('Failed to load stats'); }
  }, []);

  const fetchSqsStatus = useCallback(async () => {
    setSqsLoading(true);
    try {
      const { data } = await api.get('/super-admin/sqs/status');
      setSqsStatus(data);
    } catch { toast.error('Failed to load SQS status'); }
    finally { setSqsLoading(false); }
  }, []);

  useEffect(() => {
    if (subTab === 'queue') {
      setParserJobsSkip(0);
      fetchParserJobs(false);
      fetchParserJobsStats();
      fetchSqsStatus();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTab, parserJobsFilters]);

  // Reset pagination when filters change
  const updateFilter = (newFilters) => {
    setParserJobsSkip(0);
    setParserJobs([]);
    setParserJobsFilters(newFilters);
  };

  const handleReprocessJob = async (jobId) => {
    try {
      await api.post(`/super-admin/parser-jobs/${jobId}/reprocess`);
      toast.success('Job marked for reprocessing');
      fetchParserJobs(); fetchParserJobsStats();
    } catch { toast.error('Failed to reprocess job'); }
  };

  const handleStopJob = async (jobId) => {
    if (!window.confirm('Stop this job?')) return;
    try {
      await api.post(`/super-admin/parser-jobs/${jobId}/stop`);
      toast.success('Job cancelled');
      fetchParserJobs(); fetchParserJobsStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to stop job'); }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Delete this job?')) return;
    try {
      await api.delete(`/super-admin/parser-jobs/${jobId}`);
      toast.success('Job deleted');
      fetchParserJobs(); fetchParserJobsStats();
    } catch { toast.error('Failed to delete job'); }
  };

  const handleBulkReprocessAll = async () => {
    if (!window.confirm('Reprocess all unprocessed bills across ALL organizations?')) return;
    setBulkReprocessLoading(true);
    try {
      const { data } = await api.post('/super-admin/bulk-reprocess-all', { statuses: ['uploaded', 'processing', 'failed'], limit_per_org: 50 });
      toast.success(`Sent ${data.total_sent_to_sqs} documents to SQS across ${data.orgs_processed} organizations`);
      fetchParserJobs(); fetchParserJobsStats(); fetchSqsStatus();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to bulk reprocess'); }
    finally { setBulkReprocessLoading(false); }
  };

  const getStatusBadge = (status) => {
    const styles = { queued: 'bg-yellow-100 text-yellow-800', processing: 'bg-blue-100 text-blue-800', completed: 'bg-green-100 text-green-800', failed: 'bg-red-100 text-red-800', cancelled: 'bg-gray-100 text-gray-600' };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const getSourceBadge = (source) => {
    const styles = {
      upload: { bg: 'bg-purple-100', text: 'text-purple-800', icon: FileText },
      email: { bg: 'bg-blue-100', text: 'text-blue-800', icon: Mail },
      api: { bg: 'bg-green-100', text: 'text-green-800', icon: Cpu },
      reprocess: { bg: 'bg-orange-100', text: 'text-orange-800', icon: RefreshCw },
      sftp: { bg: 'bg-cyan-100', text: 'text-cyan-800', icon: FolderOpen },
      bulk_reprocess: { bg: 'bg-amber-100', text: 'text-amber-800', icon: Zap },
    };
    return styles[source] || { bg: 'bg-gray-100', text: 'text-gray-600', icon: FileText };
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString() : '-';

  const SUB_TABS = [
    { id: 'queue', label: 'Parser Queue', icon: Cpu },
    { id: 'docs', label: 'Technical Docs', icon: BookOpen },
  ];

  return (
    <div className="space-y-6" data-testid="parser-queue-page">
      {/* Sub-tab nav */}
      <div className="border border-border rounded-none bg-card">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <Cpu className="h-6 w-6 text-primary" />
            <div>
              <h2 className="text-xl font-bold">Parser Queue</h2>
              <p className="text-sm text-muted-foreground">Monitor all parsing jobs across the platform</p>
            </div>
          </div>
        </div>
        <div className="flex border-b border-border">
          {SUB_TABS.map((tab) => (
            <button key={tab.id} onClick={() => setSubTab(tab.id)} data-testid={`pq-subtab-${tab.id}`}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${subTab === tab.id ? 'border-primary text-primary bg-muted/30' : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/20'}`}>
              <tab.icon className="h-4 w-4" />{tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* === QUEUE TAB === */}
      {subTab === 'queue' && (
        <div className="space-y-6">
          {/* SQS Status Panel */}
          <div className="border border-border rounded-none p-4 bg-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Network className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Lambda Processing Queue (SQS)</h3>
                {sqsStatus?.sqs_enabled && <Badge className="rounded-none text-xs bg-green-500">Enabled</Badge>}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="rounded-none" onClick={fetchSqsStatus} disabled={sqsLoading} data-testid="refresh-sqs-status-btn">
                  <RefreshCw className={`h-4 w-4 mr-2 ${sqsLoading ? 'animate-spin' : ''}`} />Refresh Status
                </Button>
                <Button variant="default" size="sm" className="rounded-none bg-amber-600 hover:bg-amber-700" onClick={handleBulkReprocessAll} disabled={bulkReprocessLoading} data-testid="bulk-reprocess-all-btn">
                  <Zap className={`h-4 w-4 mr-2 ${bulkReprocessLoading ? 'animate-spin' : ''}`} />Reprocess All Orgs
                </Button>
              </div>
            </div>
            {sqsStatus ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 bg-muted/50 rounded-none"><p className="text-xs uppercase tracking-wide font-medium text-muted-foreground mb-1">Messages Available</p><p className="text-xl font-bold text-blue-600">{sqsStatus.sqs?.messages_available || 0}</p></div>
                <div className="p-3 bg-muted/50 rounded-none"><p className="text-xs uppercase tracking-wide font-medium text-muted-foreground mb-1">In Flight</p><p className="text-xl font-bold text-orange-600">{sqsStatus.sqs?.messages_in_flight || 0}</p></div>
                <div className="p-3 bg-muted/50 rounded-none"><p className="text-xs uppercase tracking-wide font-medium text-muted-foreground mb-1">Parser Jobs Queued</p><p className="text-xl font-bold text-yellow-600">{sqsStatus.parser_jobs?.queued || 0}</p></div>
                <div className="p-3 bg-muted/50 rounded-none"><p className="text-xs uppercase tracking-wide font-medium text-muted-foreground mb-1">Processing</p><p className="text-xl font-bold text-green-600">{sqsStatus.parser_jobs?.processing || 0}</p></div>
              </div>
            ) : (
              <div className="text-center py-4 text-muted-foreground"><Network className="h-8 w-8 mx-auto mb-2 opacity-30" /><p className="text-sm">Click "Refresh Status" to load SQS queue information</p></div>
            )}
          </div>

          {/* Stats Cards */}
          {parserJobsStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div className="border border-border rounded-none p-4 bg-card"><p className="text-xs uppercase tracking-wide font-medium text-muted-foreground mb-1">Total Jobs</p><p className="text-2xl font-bold">{parserJobsStats.total_jobs || 0}</p></div>
              <div className="border border-border rounded-none p-4 bg-card"><p className="text-xs uppercase tracking-wide font-medium text-yellow-600 mb-1">Queued</p><p className="text-2xl font-bold text-yellow-600">{parserJobsStats.by_status?.queued || 0}</p></div>
              <div className="border border-border rounded-none p-4 bg-card"><p className="text-xs uppercase tracking-wide font-medium text-blue-600 mb-1">Processing</p><p className="text-2xl font-bold text-blue-600">{parserJobsStats.by_status?.processing || 0}</p></div>
              <div className="border border-border rounded-none p-4 bg-card"><p className="text-xs uppercase tracking-wide font-medium text-green-600 mb-1">Completed</p><p className="text-2xl font-bold text-green-600">{parserJobsStats.by_status?.completed || 0}</p></div>
              <div className="border border-border rounded-none p-4 bg-card"><p className="text-xs uppercase tracking-wide font-medium text-red-600 mb-1">Failed</p><p className="text-2xl font-bold text-red-600">{parserJobsStats.by_status?.failed || 0}</p></div>
              <div className="border border-border rounded-none p-4 bg-card"><p className="text-xs uppercase tracking-wide font-medium text-muted-foreground mb-1">Avg Time</p><p className="text-2xl font-bold">{parserJobsStats.processing_time?.avg_duration_ms ? `${(parserJobsStats.processing_time.avg_duration_ms / 1000).toFixed(1)}s` : '-'}</p></div>
            </div>
          )}

          {/* Header + Refresh */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Bill parsing jobs from all sources</p>
            <Button variant="outline" size="sm" className="rounded-none" onClick={() => { setParserJobsSkip(0); setParserJobs([]); fetchParserJobs(false); fetchParserJobsStats(); }} disabled={parserJobsLoading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${parserJobsLoading ? 'animate-spin' : ''}`} />Refresh
            </Button>
          </div>

          {/* Search */}
          <div className="flex gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search by Job ID or Parent ID..." value={jobIdSearchInput}
                onChange={(e) => setJobIdSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && setJobIdSearch(jobIdSearchInput)}
                className="pl-9 rounded-none" data-testid="job-id-search-input" />
            </div>
            <Button variant="default" size="sm" className="rounded-none" onClick={() => setJobIdSearch(jobIdSearchInput)} data-testid="job-id-search-btn"><Search className="h-4 w-4 mr-2" />Search</Button>
            {jobIdSearch && <Button variant="ghost" size="sm" className="rounded-none" onClick={() => { setJobIdSearch(''); setJobIdSearchInput(''); }}><X className="h-4 w-4" /></Button>}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <DropdownMenu><DropdownMenuTrigger asChild><Button variant="outline" size="sm" className="rounded-none"><Building2 className="h-4 w-4 mr-2" />{parserJobsFilters.account_id === 'all' ? 'All Orgs' : accounts.find(a => a.id === parserJobsFilters.account_id)?.organisation_name?.slice(0, 15) || 'Org'}<ChevronDown className="h-4 w-4 ml-2" /></Button></DropdownMenuTrigger><DropdownMenuContent className="max-h-64 overflow-y-auto"><DropdownMenuItem onClick={() => updateFilter({ ...parserJobsFilters, account_id: 'all' })}>All Organisations</DropdownMenuItem>{accounts.map(a => <DropdownMenuItem key={a.id} onClick={() => updateFilter({ ...parserJobsFilters, account_id: a.id })}>{a.organisation_name || a.company_name}</DropdownMenuItem>)}</DropdownMenuContent></DropdownMenu>
            <DropdownMenu><DropdownMenuTrigger asChild><Button variant="outline" size="sm" className="rounded-none"><Clock className="h-4 w-4 mr-2" />{parserJobsFilters.status === 'all' ? 'All Status' : parserJobsFilters.status}<ChevronDown className="h-4 w-4 ml-2" /></Button></DropdownMenuTrigger><DropdownMenuContent>{['all', 'queued', 'processing', 'completed', 'failed', 'cancelled'].map(s => <DropdownMenuItem key={s} onClick={() => updateFilter({ ...parserJobsFilters, status: s })}>{s === 'all' ? 'All Statuses' : s.charAt(0).toUpperCase() + s.slice(1)}</DropdownMenuItem>)}</DropdownMenuContent></DropdownMenu>
            <DropdownMenu><DropdownMenuTrigger asChild><Button variant="outline" size="sm" className="rounded-none"><Upload className="h-4 w-4 mr-2" />{parserJobsFilters.source === 'all' ? 'All Sources' : parserJobsFilters.source}<ChevronDown className="h-4 w-4 ml-2" /></Button></DropdownMenuTrigger><DropdownMenuContent>{['all', 'upload', 'email', 'api', 'reprocess', 'sftp'].map(s => <DropdownMenuItem key={s} onClick={() => updateFilter({ ...parserJobsFilters, source: s })}>{s === 'all' ? 'All Sources' : s.charAt(0).toUpperCase() + s.slice(1)}</DropdownMenuItem>)}</DropdownMenuContent></DropdownMenu>
            <DropdownMenu><DropdownMenuTrigger asChild><Button variant="outline" size="sm" className="rounded-none"><Cpu className="h-4 w-4 mr-2" />{parserJobsFilters.processing_method === 'all' ? 'All Methods' : parserJobsFilters.processing_method === 'lambda' ? 'Lambda' : 'Worker'}<ChevronDown className="h-4 w-4 ml-2" /></Button></DropdownMenuTrigger><DropdownMenuContent><DropdownMenuItem onClick={() => updateFilter({ ...parserJobsFilters, processing_method: 'all' })}>All Methods</DropdownMenuItem><DropdownMenuItem onClick={() => updateFilter({ ...parserJobsFilters, processing_method: 'lambda' })}><Zap className="h-4 w-4 mr-2 text-emerald-600" />Lambda (SQS)</DropdownMenuItem><DropdownMenuItem onClick={() => updateFilter({ ...parserJobsFilters, processing_method: 'local' })}><Cpu className="h-4 w-4 mr-2 text-amber-600" />Worker (Local)</DropdownMenuItem></DropdownMenuContent></DropdownMenu>
            {(parserJobsFilters.status !== 'all' || parserJobsFilters.account_id !== 'all' || parserJobsFilters.source !== 'all' || parserJobsFilters.processing_method !== 'all') && (
              <Button variant="ghost" size="sm" className="rounded-none text-muted-foreground" onClick={() => updateFilter({ status: 'all', account_id: 'all', source: 'all', processing_method: 'all' })}><X className="h-4 w-4 mr-1" />Clear Filters</Button>
            )}
          </div>

          {/* Jobs Table */}
          <div className="border border-border rounded-none bg-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="parser-jobs-table">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Job ID</th>
                    <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Organisation</th>
                    <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Filename</th>
                    <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Source</th>
                    <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Method</th>
                    <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Status</th>
                    <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Created</th>
                    <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Duration</th>
                    <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {parserJobsLoading ? (
                    <tr><td colSpan={9} className="text-center p-8 text-muted-foreground"><RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />Loading parser jobs...</td></tr>
                  ) : parserJobs.length === 0 ? (
                    <tr><td colSpan={9} className="text-center p-8 text-muted-foreground"><Cpu className="h-8 w-8 mx-auto mb-2 opacity-50" /><p>No parser jobs found</p><p className="text-xs mt-1">Jobs appear here when bills are processed</p></td></tr>
                  ) : (
                    parserJobs
                      .filter(job => {
                        if (!jobIdSearch) return true;
                        const s = jobIdSearch.toLowerCase();
                        return job.id?.toLowerCase().includes(s) || job.document_id?.toLowerCase().includes(s) || job.parent_id?.toLowerCase().includes(s);
                      })
                      .map((job) => {
                        const sb = getSourceBadge(job.source);
                        const SIcon = sb.icon;
                        const hasParent = job.parent_id && job.parent_id !== job.document_id;
                        const childCount = job.child_count || 0;
                        return (
                          <tr key={job.id} className="border-b border-border hover:bg-muted/30">
                            <td className="p-3">
                              <div className="flex items-center gap-1">
                                <TooltipProvider><Tooltip><TooltipTrigger asChild>
                                  <span className="text-xs font-mono text-muted-foreground cursor-pointer hover:text-foreground" title={job.id}>{job.id?.slice(0, 8)}...</span>
                                </TooltipTrigger><TooltipContent side="right" className="max-w-xs"><p className="font-mono text-xs">{job.id}</p><p className="text-[10px] text-muted-foreground mt-1">Document: {job.document_id}</p>{hasParent && <p className="text-[10px] text-blue-500 mt-1">Parent: {job.parent_id}</p>}</TooltipContent></Tooltip></TooltipProvider>
                                <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => { navigator.clipboard.writeText(job.id); toast.success('Job ID copied'); }} title="Copy Job ID"><Copy className="h-3 w-3" /></Button>
                              </div>
                              <div className="flex items-center gap-1 mt-1">
                                {hasParent && (
                                  <TooltipProvider><Tooltip><TooltipTrigger asChild><Badge variant="outline" className="rounded-none text-[9px] px-1 py-0 bg-blue-50 text-blue-700 border-blue-200 cursor-pointer" onClick={() => { navigator.clipboard.writeText(job.parent_id); toast.success('Parent ID copied'); }}>P</Badge></TooltipTrigger><TooltipContent><p className="text-xs">Parent: {job.parent_id}</p><p className="text-[10px] text-muted-foreground">Click to copy</p></TooltipContent></Tooltip></TooltipProvider>
                                )}
                                {childCount > 0 && <Badge variant="outline" className="rounded-none text-[9px] px-1 py-0 bg-purple-50 text-purple-700 border-purple-200">{childCount} child{childCount > 1 ? 'ren' : ''}</Badge>}
                              </div>
                            </td>
                            <td className="p-3"><span className="text-sm font-medium">{job.organisation_name || 'Unknown'}</span></td>
                            <td className="p-3"><span className="text-sm truncate max-w-[200px] block" title={job.filename}>{job.filename || '-'}</span></td>
                            <td className="p-3"><Badge className={`rounded-none text-xs ${sb.bg} ${sb.text}`}><SIcon className="h-3 w-3 mr-1" />{job.source}</Badge></td>
                            <td className="p-3">
                              {job.processing_method === 'lambda' ? (
                                <TooltipProvider><Tooltip><TooltipTrigger asChild><Badge className="rounded-none text-xs bg-emerald-100 text-emerald-700 border-emerald-200"><Zap className="h-3 w-3 mr-1" />Lambda</Badge></TooltipTrigger><TooltipContent><p className="text-xs">Processed via AWS Lambda (SQS)</p>{job.sqs_message_id && <p className="text-[10px] text-muted-foreground mt-1">SQS: {job.sqs_message_id.slice(0, 20)}...</p>}</TooltipContent></Tooltip></TooltipProvider>
                              ) : job.processing_method === 'local' ? (
                                <TooltipProvider><Tooltip><TooltipTrigger asChild><Badge className="rounded-none text-xs bg-amber-100 text-amber-700 border-amber-200"><Cpu className="h-3 w-3 mr-1" />Worker</Badge></TooltipTrigger><TooltipContent><p className="text-xs">Processed by local worker (non-SQS)</p>{job.sqs_error && <p className="text-[10px] text-red-500 mt-1">SQS Error: {job.sqs_error}</p>}</TooltipContent></Tooltip></TooltipProvider>
                              ) : <Badge variant="outline" className="rounded-none text-xs text-muted-foreground">-</Badge>}
                            </td>
                            <td className="p-3"><Badge className={`rounded-none text-xs uppercase ${getStatusBadge(job.status)}`}>{job.status}</Badge></td>
                            <td className="p-3"><div className="flex items-center gap-1"><Clock className="h-4 w-4 text-muted-foreground" /><span className="text-xs">{formatDate(job.created_at)}</span></div></td>
                            <td className="p-3"><span className="text-xs text-muted-foreground">{job.duration_ms ? `${(job.duration_ms / 1000).toFixed(1)}s` : '-'}</span></td>
                            <td className="p-3">
                              <div className="flex items-center gap-1">
                                {(job.status === 'failed' || job.status === 'completed') && <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-none" onClick={() => handleReprocessJob(job.id)} title="Reprocess"><RotateCcw className="h-4 w-4" /></Button>}
                                {(job.status === 'queued' || job.status === 'processing') && <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-none text-orange-600 hover:text-orange-700" onClick={() => handleStopJob(job.id)} title="Stop"><XCircle className="h-4 w-4" /></Button>}
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-none text-destructive hover:text-destructive" onClick={() => handleDeleteJob(job.id)} title="Delete"><Trash2 className="h-4 w-4" /></Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {parserJobs.length > 0 && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">Showing {parserJobs.length} of {parserJobsTotalCount} job(s){parserJobsTotalCount !== parserJobs.length ? ` (filtered total: ${parserJobsTotalCount})` : ''}</p>
              {parserJobs.length < parserJobsTotalCount && (
                <Button variant="outline" size="sm" className="rounded-none" onClick={() => fetchParserJobs(true)} disabled={parserJobsLoadingMore} data-testid="load-more-parser-jobs-btn">
                  {parserJobsLoadingMore ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Loading...</> : <>Load More ({Math.min(PAGE_SIZE, parserJobsTotalCount - parserJobs.length)} more)</>}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* === TECHNICAL DOCS TAB === */}
      {subTab === 'docs' && (
        <div className="space-y-6">
          {/* Overview Cards */}
          <div className="border border-border rounded-none bg-card p-6">
            <div className="flex items-center gap-3 mb-6"><BookOpen className="h-5 w-5 text-primary" /><h3 className="font-semibold text-lg">Parser Queue Overview</h3></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DocCard title="SQS Queue Pipeline" content="Documents are sent to AWS SQS (billportal-processing-queue) for asynchronous processing by Lambda functions. Batch sending supports up to 10 messages per batch. Each message includes document_id, account_id, and org parsing config." />
              <DocCard title="Lambda vs Worker Processing" content="Primary: Lambda (SQS-triggered, auto-scaling). Fallback: Local Worker when SQS fails or is disabled. The Method column shows which path processed each job." />
              <DocCard title="Parent/Child Jobs" content="Email attachments create parent-child job relationships. A parent email job spawns child jobs for each attachment. Use the 'P' badge and parent ID to trace relationships in the table." />
              <DocCard title="Job Lifecycle" content="queued → processing → completed/failed/cancelled. Duration tracked in ms. Failed jobs retain error messages. Reprocess sends back to SQS for another Lambda attempt." />
            </div>
          </div>

          {/* Architecture & Workflow */}
          <div className="border border-border rounded-none bg-card p-6" data-testid="pq-architecture-section">
            <div className="flex items-center gap-3 mb-6"><Zap className="h-5 w-5 text-primary" /><h3 className="font-semibold text-lg">Architecture &amp; Workflow</h3></div>
            <div className="space-y-4">
              <div className="border border-border p-4 bg-muted/20">
                <h4 className="font-semibold mb-2">End-to-End Parsing Workflow</h4>
                <div className="font-mono text-xs text-muted-foreground bg-muted/50 p-3 border border-border space-y-1">
                  <p>{'  '}Document Ingestion (UI Upload / Email / API / SFTP)</p>
                  <p>{'           '}|</p>
                  <p>{'           '}v</p>
                  <p>{'  '}BillPortal Backend (FastAPI)</p>
                  <p>{'  '}  - Document stored in S3</p>
                  <p>{'  '}  - document record created in MongoDB</p>
                  <p>{'  '}  - parser_jobs record created (status=queued)</p>
                  <p>{'           '}|</p>
                  <p>{'           '}v</p>
                  <p>{'  '}AWS SQS (billportal-processing-queue)</p>
                  <p>{'  '}  - JSON message: document_id, account_id, parsing_config</p>
                  <p>{'  '}  - At-least-once delivery guarantee</p>
                  <p>{'           '}|</p>
                  <p>{'           '}v</p>
                  <p>{'  '}AWS Lambda (triggered by SQS, auto-scaling)</p>
                  <p>{'  '}  - Downloads document from S3</p>
                  <p>{'  '}  - OCR + text extraction + AI parsing</p>
                  <p>{'  '}  - Updates parser_jobs (status, duration_ms)</p>
                  <p>{'           '}|</p>
                  <p>{'           '}v</p>
                  <p>{'  '}Parsed data stored in bills + line_items collections</p>
                  <p>{'  '}Available for review in BillPortal Intake Hub</p>
                  <p></p>
                  <p>{'  '}Fallback: If SQS fails → local Worker processing</p>
                </div>
              </div>
              <div className="border border-border p-4 bg-muted/20">
                <h4 className="font-semibold mb-2">Component Architecture</h4>
                <div className="font-mono text-xs text-muted-foreground bg-muted/50 p-3 border border-border space-y-1">
                  <p>+------------------+   +------------------+   +------------------+</p>
                  <p>| Document Sources  |   | BillPortal API   |   | S3 Storage       |</p>
                  <p>| - UI Upload      | --{'>'} | - Creates jobs   | --{'>'} | - Original docs  |</p>
                  <p>| - Email Inbox    |   | - Sends to SQS   |   | - OCR outputs    |</p>
                  <p>| - API / SFTP     |   +------------------+   +------------------+</p>
                  <p>+------------------+           |</p>
                  <p>{'                              '}v</p>
                  <p>{'                     '}+------------------+</p>
                  <p>{'                     '}| AWS SQS          |</p>
                  <p>{'                     '}| billportal-      |</p>
                  <p>{'                     '}| processing-queue |</p>
                  <p>{'                     '}+------------------+</p>
                  <p>{'                              '}|</p>
                  <p>{'                     '}+-------+--------+</p>
                  <p>{'                     '}|                |</p>
                  <p>{'                     '}v                v</p>
                  <p>{'            '}+--------------+  +--------------+</p>
                  <p>{'            '}| Lambda Fn    |  | Lambda Fn    |</p>
                  <p>{'            '}| (auto-scale) |  | (auto-scale) |</p>
                  <p>{'            '}+--------------+  +--------------+</p>
                  <p>{'                     '}|</p>
                  <p>{'                     '}v</p>
                  <p>{'            '}+-------------------------------+</p>
                  <p>{'            '}| MongoDB: parser_jobs (status) |</p>
                  <p>{'            '}| MongoDB: bills + line_items   |</p>
                  <p>{'            '}+-------------------------------+</p>
                </div>
              </div>
            </div>
          </div>

          {/* Code References */}
          <div className="border border-border rounded-none bg-card p-6" data-testid="pq-code-references">
            <div className="flex items-center gap-3 mb-6"><Info className="h-5 w-5 text-primary" /><h3 className="font-semibold text-lg">Code References &amp; API</h3></div>
            <div className="space-y-4">
              <div className="border border-border p-4 bg-muted/20">
                <h4 className="font-semibold mb-2">Backend Files</h4>
                <div className="font-mono text-xs text-muted-foreground bg-muted/50 p-3 border border-border space-y-2">
                  <p className="text-foreground font-semibold">server.py</p>
                  <p>{'  '}SQSService class                  — SQS client, send_document_for_processing()</p>
                  <p>{'  '}GET  /api/super-admin/parser-jobs       — List jobs (filterable, paginated)</p>
                  <p>{'  '}GET  /api/super-admin/parser-jobs/stats — Aggregated stats by status/source</p>
                  <p>{'  '}POST /api/super-admin/parser-jobs/:id/reprocess — Re-send to SQS</p>
                  <p>{'  '}POST /api/super-admin/parser-jobs/:id/stop      — Cancel queued/processing</p>
                  <p>{'  '}DELETE /api/super-admin/parser-jobs/:id          — Remove job record</p>
                  <p>{'  '}GET  /api/super-admin/sqs/status                — SQS queue depth + in-flight</p>
                  <p>{'  '}POST /api/super-admin/bulk-reprocess-all        — Reprocess all orgs</p>
                  <p>{'  '}POST /api/super-admin/accounts/:id/bulk-reprocess — Per-org reprocess</p>
                  <p></p>
                  <p className="text-foreground font-semibold">Document Upload Flow</p>
                  <p>{'  '}POST /api/accounts/:id/documents/upload — Upload + create parser_job + SQS</p>
                  <p>{'  '}create_document_from_content()          — Email path: S3 + parser_job + SQS</p>
                  <p>{'  '}extract_data_from_pdf()                 — Local Worker fallback path</p>
                </div>
              </div>
              <div className="border border-border p-4 bg-muted/20">
                <h4 className="font-semibold mb-2">Frontend Files</h4>
                <div className="font-mono text-xs text-muted-foreground bg-muted/50 p-3 border border-border space-y-2">
                  <p className="text-foreground font-semibold">pages/cockpit/ParserQueuePage.js</p>
                  <p>{'  '}fetchParserJobs()      — Paginated job list (skip/limit)</p>
                  <p>{'  '}fetchParserJobsStats() — Dashboard stat cards</p>
                  <p>{'  '}fetchSqsStatus()       — SQS queue panel</p>
                  <p>{'  '}handleBulkReprocessAll()— Bulk reprocess all orgs</p>
                  <p></p>
                  <p className="text-foreground font-semibold">pages/Cockpit.js</p>
                  <p>{'  '}Main layout with UBM Sync / Parser Queue / Email Queue tabs</p>
                  <p>{'  '}Fetches accounts list for org filter dropdowns</p>
                </div>
              </div>
              <div className="border border-border p-4 bg-muted/20">
                <h4 className="font-semibold mb-2">Collections</h4>
                <div className="font-mono text-xs text-muted-foreground bg-muted/50 p-3 border border-border space-y-1">
                  <p>parser_jobs      — Job tracking (id, document_id, account_id, status, source,</p>
                  <p>{'                  '}processing_method, sqs_message_id, duration_ms, created_at)</p>
                  <p>documents        — Document metadata (id, account_id, filename, file_path, status)</p>
                  <p>bills            — Parsed bill headers (vendor, amount, dates, account mapping)</p>
                  <p>line_items       — Parsed charge line items (linked to bills)</p>
                  <p>org_parsing_config — Per-org parsing settings (OCR engine, validation flags)</p>
                </div>
              </div>
              <div className="border border-border p-4 bg-muted/20">
                <h4 className="font-semibold mb-2">Environment Variables</h4>
                <div className="font-mono text-xs text-muted-foreground bg-muted/50 p-3 border border-border space-y-1">
                  <p>SQS_QUEUE_URL         — AWS SQS queue URL</p>
                  <p>AWS_ACCESS_KEY_ID     — AWS credentials for SQS/S3</p>
                  <p>AWS_SECRET_ACCESS_KEY — AWS secret key</p>
                  <p>AWS_REGION            — AWS region (default: us-east-1)</p>
                  <p>USE_SQS_PROCESSING    — Enable/disable SQS (true/false)</p>
                  <p>S3_BUCKET_NAME        — S3 bucket for document storage</p>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div className="border border-border rounded-none bg-card p-6" data-testid="pq-faq-section">
            <div className="flex items-center gap-3 mb-6"><HelpCircle className="h-5 w-5 text-primary" /><h3 className="font-semibold text-lg">FAQ</h3></div>
            <div className="space-y-4">
              <FaqItem q="How does a document get into the parser queue?" a="When a document is uploaded (via UI, email, API, or SFTP), BillPortal creates a parser_jobs record and sends the document to the AWS SQS queue. The SQS message triggers a Lambda function for processing." />
              <FaqItem q="What do the Lambda and Worker badges mean?" a="Lambda = processed via AWS Lambda (SQS). Worker = processed by local worker (fallback when SQS fails or is unavailable). SQS errors are shown in Worker badge tooltips." />
              <FaqItem q="Why does Total Jobs show fewer than total documents?" a="parser_jobs collection only tracks jobs since the SQS pipeline was implemented. Older documents processed before this feature was added won't have parser_jobs records." />
              <FaqItem q="How do I reprocess a failed job?" a="Click the circular arrow (Reprocess) icon in the Actions column. This re-sends the document to SQS for another Lambda attempt." />
              <FaqItem q="What does 'Reprocess All Orgs' do?" a="Sends ALL unprocessed and failed bills across every organization to SQS for Lambda processing. Use with caution — this can create a large SQS backlog." />
              <FaqItem q="How do parent/child jobs work?" a="When an email has multiple PDF attachments, the email creates a parent job, and each attachment creates a child job. The 'P' badge indicates a parent exists." />
              <FaqItem q="What do Messages Available and In Flight mean?" a="Messages Available = SQS messages waiting to be picked up by Lambda. In Flight = messages currently being processed by Lambda. Both should be 0 when idle." />
            </div>
          </div>
        </div>
      )}

      {/* Docs Drawer - removed (all docs consolidated into Technical Docs tab above) */}
    </div>
  );
}

function DocCard({ title, content }) {
  return (<div className="border border-border p-4 bg-muted/20"><h4 className="font-semibold text-sm mb-2">{title}</h4><p className="text-xs text-muted-foreground leading-relaxed">{content}</p></div>);
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (<div className="border border-border"><button className="w-full p-4 text-left flex items-center justify-between hover:bg-muted/20 transition-colors" onClick={() => setOpen(!open)}><span className="font-medium text-sm">{q}</span>{open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}</button>{open && <div className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed border-t border-border pt-3">{a}</div>}</div>);
}
